/**
 *
 */
import * as React from 'react';
import { observer } from 'mobx-react';
import { Container } from 'reactstrap';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { Link, useLocation } from 'react-router-dom';
import { QRCodeCanvas } from 'qrcode.react';
import { useAuth0 } from '@auth0/auth0-react';
import { useTrans } from './Store/System/LocalizationStore';
import { Theme } from './Theme/Theme';
import { Button } from './Components/Button';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const PageContainer = styled(Container)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  text-align: center;
  padding: 48px 16px;
`;

const FullPageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  text-align: center;
  padding: 48px 16px;
  background: linear-gradient(180deg, #1a1a2e 0%, #16213e 100%);
  animation: ${fadeIn} 0.6s ease-out;
`;

const LogoImage = styled.img`
  width: 120px;
  height: 120px;
  margin-bottom: 32px;
  border-radius: 24px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
`;

const UnauthTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 800;
  color: white;
  margin: 0 0 16px 0;
  
  @media (max-width: 600px) {
    font-size: 1.75rem;
  }
`;

const UnauthDescription = styled.p`
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.7);
  max-width: 400px;
  margin: 0 0 32px 0;
  line-height: 1.6;
`;

const LoginButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, #00dda2 0%, #007763 100%);
  color: white;
  padding: 16px 32px;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 700;
  text-decoration: none;
  text-transform: uppercase;
  letter-spacing: 1px;
  box-shadow: 0 4px 16px rgba(36, 220, 164, 0.3);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(36, 220, 164, 0.4);
    color: white;
    text-decoration: none;
  }
`;

const UnauthQrSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 48px;
  padding: 24px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(12px);
`;

const UnauthQrTitle = styled.p`
  font-size: 0.9rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  margin: 0 0 8px 0;
`;

const UnauthQrDescription = styled.p`
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.5);
  margin: 0 0 16px 0;
`;

const UnauthQrCodeWrapper = styled.div`
  background: white;
  padding: 12px;
  border-radius: 12px;
`;

const ErrorCode = styled.h1`
  font-size: 8rem;
  font-weight: 800;
  color: ${Theme.Colors.primary};
  margin: 0;
  line-height: 1;

  @media (max-width: 600px) {
    font-size: 5rem;
  }
`;

const Title = styled.h2`
  font-size: 2rem;
  font-weight: 700;
  color: ${Theme.Colors.black};
  margin: 16px 0 8px 0;

  @media (max-width: 600px) {
    font-size: 1.5rem;
  }
`;

const Description = styled.p`
  font-size: 1.1rem;
  color: ${Theme.Colors.charcoal};
  max-width: 500px;
  margin: 0 0 32px 0;
`;

const QuickLinksContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  margin-top: 16px;
`;

const QuickLinksTitle = styled.p`
  font-size: 0.9rem;
  color: ${Theme.Colors.charcoal};
  margin: 0;
`;

const LinksRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;
`;

const QuickLink = styled(Link)`
  padding: 10px 20px;
  border-radius: 8px;
  background-color: ${Theme.Colors.lightestGrey};
  color: ${Theme.Colors.black};
  text-decoration: none;
  font-weight: 500;
  font-size: 0.9rem;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${Theme.Colors.lightGrey};
    color: ${Theme.Colors.black};
    text-decoration: none;
  }
`;

const HomeButton = styled(Link)`
  text-decoration: none;

  &:hover {
    text-decoration: none;
  }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  max-width: 400px;
  margin: 32px 0;
  gap: 16px;
`;

const DividerLine = styled.div`
  flex: 1;
  height: 1px;
  background-color: ${Theme.Colors.lightGrey};
`;

const DividerText = styled.span`
  font-size: 0.85rem;
  color: ${Theme.Colors.charcoal};
  text-transform: uppercase;
`;

const QrSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px;
  background-color: ${Theme.Colors.lightestGrey};
  border-radius: 16px;
  max-width: 320px;
`;

const QrTitle = styled.p`
  font-size: 1rem;
  font-weight: 600;
  color: ${Theme.Colors.black};
  margin: 0 0 8px 0;
`;

const QrDescription = styled.p`
  font-size: 0.85rem;
  color: ${Theme.Colors.charcoal};
  margin: 0 0 16px 0;
`;

const QrCodeWrapper = styled.div`
  background: white;
  padding: 12px;
  border-radius: 12px;
`;

export type NotFoundScreenProps = {};

export const NotFoundScreen: React.FC<NotFoundScreenProps> = observer(() => {
  const { t } = useTrans();
  const location = useLocation();
  const { isAuthenticated } = useAuth0();

  const fullUrl = `https://coach.kinastic.com${location.pathname}${location.search}`;
  const dynamicLink = `https://open.kinastic.com/?link=${encodeURIComponent(
    fullUrl,
  )}&apn=app.flinq.athlete&ibi=app.flinq.athlete&isi=1465156373&efr=1`;

  // Unauthenticated: show simplified full-page view with login CTA
  if (!isAuthenticated) {
    return (
      <FullPageContainer>
        <LogoImage src={require('./Assets/Images/logo.png')} alt="KINASTIC" />
        <UnauthTitle>
          {t('notFoundScreen.unauthTitle', { defaultValue: 'Page Not Found' })}
        </UnauthTitle>
        <UnauthDescription>
          {t('notFoundScreen.unauthDescription', {
            defaultValue: 'Sign in to access KINASTIC and explore our health & wellness content.',
          })}
        </UnauthDescription>
        <LoginButton to="/auth">
          {t('notFoundScreen.signIn', { defaultValue: 'Sign In' })}
        </LoginButton>

        <UnauthQrSection>
          <UnauthQrTitle>
            {t('notFoundScreen.qr.title', { defaultValue: 'Looking for app content?' })}
          </UnauthQrTitle>
          <UnauthQrDescription>
            {t('notFoundScreen.qr.description', {
              defaultValue: 'Scan this QR code with your phone to open in the KINASTIC app',
            })}
          </UnauthQrDescription>
          <UnauthQrCodeWrapper>
            <QRCodeCanvas
              value={dynamicLink}
              size={140}
              imageSettings={{
                src: require('./Assets/Images/logo.png'),
                width: 28,
                height: 28,
                excavate: true,
              }}
            />
          </UnauthQrCodeWrapper>
        </UnauthQrSection>
      </FullPageContainer>
    );
  }

  // Authenticated: show full 404 page with navigation
  return (
    <PageContainer>
      <ErrorCode>404</ErrorCode>
      <Title>
        {t('notFoundScreen.title', { defaultValue: 'Oops! Page not found' })}
      </Title>
      <Description>
        {t('notFoundScreen.description', {
          defaultValue: "The page you're looking for doesn't exist or has been moved. Let's get you back on track!",
        })}
      </Description>

      <HomeButton to="/home">
        <Button color="primary">
          {t('notFoundScreen.backToHome', { defaultValue: 'Back to Home' })}
        </Button>
      </HomeButton>

      <QuickLinksContainer>
        <QuickLinksTitle>
          {t('notFoundScreen.quickLinks', { defaultValue: 'Or explore these sections:' })}
        </QuickLinksTitle>
        <LinksRow>
          <QuickLink to="/move">
            {t('notFoundScreen.link.move', { defaultValue: 'Move' })}
          </QuickLink>
          <QuickLink to="/nutrition">
            {t('notFoundScreen.link.nutrition', { defaultValue: 'Nutrition' })}
          </QuickLink>
          <QuickLink to="/academy">
            {t('notFoundScreen.link.academy', { defaultValue: 'Academy' })}
          </QuickLink>
          <QuickLink to="/schedule">
            {t('notFoundScreen.link.schedule', { defaultValue: 'Schedule' })}
          </QuickLink>
        </LinksRow>
      </QuickLinksContainer>

      <Divider>
        <DividerLine />
        <DividerText>{t('notFoundScreen.or', { defaultValue: 'or' })}</DividerText>
        <DividerLine />
      </Divider>

      <QrSection>
        <QrTitle>
          {t('notFoundScreen.qr.title', { defaultValue: 'Looking for app content?' })}
        </QrTitle>
        <QrDescription>
          {t('notFoundScreen.qr.description', {
            defaultValue: 'Scan this QR code with your phone to open in the KINASTIC app',
          })}
        </QrDescription>
        <QrCodeWrapper>
          <QRCodeCanvas
            value={dynamicLink}
            size={160}
            imageSettings={{
              src: require('./Assets/Images/logo.png'),
              width: 32,
              height: 32,
              excavate: true,
            }}
          />
        </QrCodeWrapper>
      </QrSection>
    </PageContainer>
  );
});
